import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Status, StatusSearchParams } from '../../services/statusApi';

// State interfaces
export interface StatusState {
  // Data
  items: Status[];
  selectedItem: Status | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    StatusName: string;
    Description: string;
    Active: boolean;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: StatusSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: StatusState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    StatusName: '',
    Description: '',
    Active: true,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    sortBy: 'StatusName',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Statistics
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const statusSlice = createSlice({
  name: 'status',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<Status[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<Status | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<Status>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<Status>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<StatusState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
      // Clear validation errors when form data changes
      if (state.validationErrors.length > 0) {
        state.validationErrors = [];
      }
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
      state.validationErrors = [];
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<StatusSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<StatusState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<StatusState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetStatusState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setStatistics,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetStatusState,
} = statusSlice.actions;

// Saga action types
export const FETCH_STATUSES = 'status/fetchStatuses';
export const FETCH_STATUS_BY_ID = 'status/fetchStatusById';
export const CREATE_STATUS = 'status/createStatus';
export const UPDATE_STATUS = 'status/updateStatus';
export const DELETE_STATUS = 'status/deleteStatus';
export const SEARCH_STATUSES = 'status/searchStatuses';
export const FETCH_STATISTICS = 'status/fetchStatistics';
export const BULK_ACTIVATE = 'status/bulkActivate';
export const BULK_DEACTIVATE = 'status/bulkDeactivate';
export const BULK_DELETE = 'status/bulkDelete';
export const EXPORT_TO_CSV = 'status/exportToCSV';

// Saga action creators
export const fetchStatuses = (params?: StatusSearchParams) => ({ type: FETCH_STATUSES, payload: params });
export const fetchStatusById = (id: number) => ({ type: FETCH_STATUS_BY_ID, payload: id });
export const createStatus = (status: Omit<Status, 'Id'>) => ({ type: CREATE_STATUS, payload: status });
export const updateStatus = (id: number, status: Partial<Status>) => ({ type: UPDATE_STATUS, payload: { id, status } });
export const deleteStatus = (id: number) => ({ type: DELETE_STATUS, payload: id });
export const searchStatuses = (params: StatusSearchParams) => ({ type: SEARCH_STATUSES, payload: params });
export const fetchStatistics = () => ({ type: FETCH_STATISTICS });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: StatusSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default statusSlice.reducer; 